import {
  connect,
  MapDispatchToProps,
  MapStateToProps,
  MergeProps,
} from 'react-redux'
import { actions, getters, selectors } from '@opallabs/wormhole'
import { NormalizedTask, SubTab, State } from '@opallabs/wormhole/types'
import {
  getAssignmentParamsForTasksByType,
  getPhaseItemParamsForTasksByType,
} from './getFiltersByTaskType'
import {
  togglePhaseItemStarredTaskParams,
  togglePhaseItemCompleteTaskParams,
  toggleAssignmentStarredTaskParams,
  toggleAssignmentCompleteTaskParams,
} from './helpers'
import { DispatchProps, MergedProps, OwnProps, StateProps } from './types'
import { TaskPanel } from '.'

const mapStateToProps: MapStateToProps<StateProps, OwnProps, State> = (
  state
) => {
  const loadingStates = getters.getPersonalPanelLoadingStates(state)
  return {
    // undefined here means we have not attempted to call the API yet
    todoTasksLoading: loadingStates ? loadingStates.tasks.todo : false,
    overdueTasksLoading: loadingStates ? loadingStates.tasks.overdue : false,
    upcomingTasksLoading: loadingStates ? loadingStates.tasks.upcoming : false,
    completedTasksLoading: loadingStates
      ? loadingStates.tasks.completed
      : false,
    brandId: selectors.selectCurrentBrandId(state),
    tasks: {
      todo: selectors.selectTodoTasks(state),
      overdue: selectors.selectOverdueTasks(state),
      upcoming: selectors.selectUpcomingTasks(state),
      completed: selectors.selectCompletedTasks(state),
    },
  }
}

const mapDispatchToProps: MapDispatchToProps<DispatchProps, OwnProps> = (
  dispatch,
  ownProps
) => {
  const getTasks = (type: string, brandId: string): void => {
    dispatch(
      actions.assignmentsFetchRequest({
        assignmentParams: getAssignmentParamsForTasksByType(
          type,
          ownProps.user.id,
          brandId
        ),
        phaseItemParams: getPhaseItemParamsForTasksByType(
          type,
          ownProps.user.id,
          brandId
        ),
        viewKey: `tasks.${type}`,
      })
    )
  }
  return {
    getTodoTasks: (brandId: string): void => getTasks('todo', brandId),
    getOverdueTasks: (brandId: string): void => getTasks('overdue', brandId),
    getUpcomingTasks: (brandId: string): void => getTasks('upcoming', brandId),
    getCompletedTasks: (brandId: string): void =>
      getTasks('completed', brandId),
    createCheckpoint: (subTab: SubTab, tasks: NormalizedTask[]): void => {
      dispatch(
        actions.createCheckpoints({
          tab: 'Task',
          subTab,
          items: tasks,
        })
      )
    },
    setTaskPreviewCard: (task: NormalizedTask | null): void => {
      ownProps.setTaskPreviewCard(task)
    },
    onTaskCheckboxClick: (task: NormalizedTask): void => {
      if (task.entityType === 'phase_item') {
        dispatch(
          actions.updatePhaseItem(
            togglePhaseItemCompleteTaskParams(task),
            undefined,
            'personalPanel'
          )
        )
      } else if (task.entityType === 'assignment') {
        dispatch(
          actions.createResponse(toggleAssignmentCompleteTaskParams(task))
        )
      }
    },
    onTaskStarClick: (task: NormalizedTask): void => {
      if (task.entityType === 'phase_item') {
        dispatch(
          actions.updatePhaseItem(
            togglePhaseItemStarredTaskParams(task),
            undefined,
            'personalPanel'
          )
        )
      } else if (task.entityType === 'assignment') {
        dispatch(
          actions.updateAssignment(
            toggleAssignmentStarredTaskParams(task),
            task.id
          )
        )
      }
    },
  }
}

const mergeProps: MergeProps<
  StateProps,
  DispatchProps,
  OwnProps,
  MergedProps
> = (stateProps, dispatchProps, ownProps) => ({
  ...stateProps,
  ...dispatchProps,
  ...ownProps,
  getCompletedTasks: (): void =>
    dispatchProps.getCompletedTasks(stateProps.brandId),
  getOverdueTasks: (): void =>
    dispatchProps.getOverdueTasks(stateProps.brandId),
  getTodoTasks: (): void => dispatchProps.getTodoTasks(stateProps.brandId),
  getUpcomingTasks: (): void =>
    dispatchProps.getUpcomingTasks(stateProps.brandId),
})

export const ConnectedTaskPanel = connect(
  mapStateToProps,
  mapDispatchToProps,
  mergeProps
)(TaskPanel)
